import { itApiFetch } from "../../services/itApi";

function buildQuery(params = {}) {
	const searchParams = new URLSearchParams();

	Object.entries(params).forEach(([key, value]) => {
		if (value === undefined || value === null || value === "") {
			return;
		}
		searchParams.append(key, String(value));
	});

	const queryString = searchParams.toString();
	return queryString ? `?${queryString}` : "";
}

async function parseJsonResponse(response, fallbackMessage) {
	let payload = {};
	try {
		payload = await response.json();
	} catch {
		payload = {};
	}

	if (!response.ok) {
		throw new Error(payload.error || fallbackMessage);
	}

	return payload;
}

async function createTicketRelation({ sourceType, sourceID, targetType, targetID, relationType = "related" }) {
	const response = await itApiFetch("/api/it/ticket-relations", {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify({
			source_type: sourceType,
			source_id: sourceID,
			target_type: targetType,
			target_id: targetID,
			relation_type: relationType,
		}),
	});

	const payload = await parseJsonResponse(response, "Failed to create ticket relation");
	return payload.relation;
}

export async function deleteTicketRelation(relationId) {
	const response = await itApiFetch(`/api/it/ticket-relations/${relationId}`, {
		method: "DELETE",
	});

	return parseJsonResponse(response, "Failed to unlink tickets");
}

export async function getTicketComments(ticketType, ticketId, filters = {}) {
	const response = await itApiFetch(`/api/it/ticket-comments${buildQuery({ ticket_type: ticketType, ticket_id: ticketId, ...filters })}`, {
		method: "GET",
	});

	const payload = await parseJsonResponse(response, "Failed to fetch work notes");
	return payload.comments || [];
}

export async function createTicketComment(ticketType, ticketId, body) {
	const response = await itApiFetch("/api/it/ticket-comments", {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify({
			ticket_type: ticketType,
			ticket_id: ticketId,
			body,
		}),
	});

	const payload = await parseJsonResponse(response, "Failed to create work note");
	return payload.comment;
}

export async function getTicketActivity(ticketType, ticketId, filters = {}) {
	const response = await itApiFetch(`/api/it/ticket-activity${buildQuery({ ticket_type: ticketType, ticket_id: ticketId, ...filters })}`, {
		method: "GET",
	});

	const payload = await parseJsonResponse(response, "Failed to fetch activity");
	return payload.events || [];
}

export async function getIncidents(filters = {}) {
	const response = await itApiFetch(`/api/it/incidents${buildQuery(filters)}`, {
		method: "GET",
	});

	const payload = await parseJsonResponse(response, "Failed to fetch incidents");
	return payload.incidents || [];
}

export async function getClients() {
	const response = await itApiFetch("/api/it/clients", {
		method: "GET",
	});

	const payload = await parseJsonResponse(response, "Failed to fetch clients");
	return payload.clients || [];
}

export async function createIncident(incidentData) {
	const response = await itApiFetch("/api/it/incidents", {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify(incidentData),
	});

	const payload = await parseJsonResponse(response, "Failed to create incident");
	return payload.incident;
}

export async function updateIncident(incidentId, updates) {
	const response = await itApiFetch(`/api/it/incidents/${incidentId}`, {
		method: "PATCH",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify(updates),
	});

	const payload = await parseJsonResponse(response, "Failed to update incident");
	return payload.incident;
}

export async function linkIncidentToProblem(incidentId, problemId) {
	const response = await itApiFetch(`/api/it/incidents/${incidentId}/link-problem`, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify({ problem_id: problemId }),
	});

	const payload = await parseJsonResponse(response, "Failed to link incident to problem");
	return payload.incident;
}

export async function createProblemFromIncident(incidentId, problemData) {
	const response = await itApiFetch(`/api/it/incidents/${incidentId}/create-problem`, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify(problemData),
	});

	const payload = await parseJsonResponse(response, "Failed to create problem from incident");
	return payload;
}

export async function getIncidentRelatedGraph(incidentId, filters = {}) {
	const response = await itApiFetch(`/api/it/incidents/${incidentId}/related-graph${buildQuery(filters)}`, {
		method: "GET",
	});

	return parseJsonResponse(response, "Failed to fetch related tickets");
}

export async function getProblemRelatedGraph(problemId, filters = {}) {
	const response = await itApiFetch(`/api/it/problems/${problemId}/related-graph${buildQuery(filters)}`, {
		method: "GET",
	});

	return parseJsonResponse(response, "Failed to fetch related tickets");
}

export async function getChangeRelatedGraph(changeId, filters = {}) {
	const response = await itApiFetch(`/api/it/changes/${changeId}/related-graph${buildQuery(filters)}`, {
		method: "GET",
	});

	return parseJsonResponse(response, "Failed to fetch related tickets");
}

export async function getProblems(filters = {}) {
	const response = await itApiFetch(`/api/it/problems${buildQuery(filters)}`, {
		method: "GET",
	});

	const payload = await parseJsonResponse(response, "Failed to fetch problems");
	return payload.problems || [];
}

export async function createProblem(problemData) {
	const response = await itApiFetch("/api/it/problems", {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify(problemData),
	});

	const payload = await parseJsonResponse(response, "Failed to create problem");
	return payload.problem;
}

export async function updateProblem(problemId, updates) {
	const response = await itApiFetch(`/api/it/problems/${problemId}`, {
		method: "PATCH",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify(updates),
	});

	const payload = await parseJsonResponse(response, "Failed to update problem");
	return payload.problem;
}

export async function deleteProblem(problemId) {
	const response = await itApiFetch(`/api/it/problems/${problemId}`, {
		method: "DELETE",
	});

	const payload = await parseJsonResponse(response, "Failed to delete problem");
	return payload.problem;
}

export async function linkProblemToChange(problemId, changeId) {
	const response = await itApiFetch(`/api/it/problems/${problemId}/link-change`, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify({ change_id: changeId }),
	});

	const payload = await parseJsonResponse(response, "Failed to link problem to change");
	return payload.problem;
}

export async function createChangeFromProblem(problemId, changeData) {
	const response = await itApiFetch(`/api/it/problems/${problemId}/create-change`, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify(changeData),
	});

	const payload = await parseJsonResponse(response, "Failed to create change from problem");
	return payload;
}

export async function getChanges(filters = {}) {
	const response = await itApiFetch(`/api/it/changes${buildQuery(filters)}`, {
		method: "GET",
	});

	const payload = await parseJsonResponse(response, "Failed to fetch changes");
	return payload.changes || [];
}

export async function createChange(changeData) {
	const response = await itApiFetch("/api/it/changes", {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify(changeData),
	});

	const payload = await parseJsonResponse(response, "Failed to create change");
	return payload.change;
}

export async function updateChange(changeId, updates) {
	const response = await itApiFetch(`/api/it/changes/${changeId}`, {
		method: "PATCH",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify(updates),
	});

	const payload = await parseJsonResponse(response, "Failed to update change");
	return payload.change;
}

export async function deleteChange(changeId) {
	const response = await itApiFetch(`/api/it/changes/${changeId}`, {
		method: "DELETE",
	});

	const payload = await parseJsonResponse(response, "Failed to delete change");
	return payload.change;
}

export async function linkProblemToIncident(problemId, incidentId) {
	return createTicketRelation({
		sourceType: "problem",
		sourceID: Number(problemId),
		targetType: "incident",
		targetID: Number(incidentId),
	});
}

export async function linkChangeToProblem(changeId, problemId) {
	return createTicketRelation({
		sourceType: "change",
		sourceID: Number(changeId),
		targetType: "problem",
		targetID: Number(problemId),
	});
}